// src/app/session/register/register.component.ts
import { Component, AfterViewInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { FormsModule } from '@angular/forms';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FooterComponent } from '../../components/footer/footer.component';
import { LanguageSwitcherComponent } from '../../components/language-switcher/language-switcher.component';
import { ModalGeneralComponent } from '../../dashboard/user/modals/modal-general/modal-general.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Meta, Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { OPEN_POSITIONS, OpenPosition } from './open-positions';
import { DEFAULT_LANGUAGE, Language, isSupportedLanguage } from '../../utils/language';
@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    RouterModule,
    MatDialogModule,
    FooterComponent,
    LanguageSwitcherComponent,
    TranslateModule
  ],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss', '../../app.component.scss']
})

export class RegisterComponent implements AfterViewInit, OnDestroy {
  registerForm: FormGroup;
  user: any = {};
  errorMessage: string = '';
  isLoading: boolean = false;
  linkedinUrl: string = '';
  autofill: boolean = false ;
  errorPhone: boolean = false;
  offer: string = '';
  readonly positions = OPEN_POSITIONS;
  // Positions shown before "show more"
  readonly visiblePositions = 6;
  showAllPositions = false;
  private queryParamsSubscription?: Subscription;
  // App-wide defaults from index.html, restored when leaving the page. Not read from the
  // DOM because a server-rendered page already carries this page's own tags.
  private readonly defaultDescription = 'La plataforma definitiva para que miles de profesionales y empresas encajen con precisión y en un tiempo récord.';
  private seoSubscription?: Subscription;
  private canonicalLink?: HTMLLinkElement;
  private readonly canonicalUrl = 'https://conectio.es/session/register';
  constructor(private http: HttpClient, private fb: FormBuilder, private router: Router, private route: ActivatedRoute, private dialog: MatDialog, private translate: TranslateService, private meta: Meta, private title: Title, @Inject(PLATFORM_ID) private platformId: Object, @Inject(DOCUMENT) private document: Document) {
    this.setSeoTags();

    this.registerForm = this.fb.group({
      name: ['', Validators.required],
      phone: ['', [Validators.required, Validators.pattern(/^(?:(?:\+34)?\d{9,10}|\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})$/)]],
      terms: [false, Validators.requiredTrue]
    });

    this.linkedinUrl = environment.linkedinUrl;
    const referredBy = this.route.snapshot.queryParamMap.get('referredBy');

    // Subscribed (not a snapshot) because picking a position changes ?offer= without reloading the page
    this.queryParamsSubscription = this.route.queryParamMap.subscribe(params => {
      this.offer = params.get('offer') || '';
      if (this.offer && isPlatformBrowser(this.platformId)) {
        document.cookie = `offer=${this.offer}; path=/;`;
      }
    });

    // Cookies only exist in the browser; skipping them lets the page render on the server
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (referredBy) {
      document.cookie = `referredBy=${referredBy}; path=/;`;

    }

    const name_cookie = document.cookie.split('; ').find(row => row.startsWith('name='))?.split('=')[1];
    const phone_cookie = document.cookie.split('; ').find(row => row.startsWith('phone='))?.split('=')[1];
    if (name_cookie || phone_cookie) {
      this.registerForm.patchValue({
        name: name_cookie,
        phone: phone_cookie,
        terms: false
      });
    }
  }

  ngAfterViewInit() {

    this.registerForm.patchValue({
      terms: false
    });
 
  }

  ngOnDestroy() {
    this.queryParamsSubscription?.unsubscribe();
    this.seoSubscription?.unsubscribe();
    // The tab title of the next page comes from its route (TranslatedTitleStrategy)
    this.meta.updateTag({ name: 'description', content: this.defaultDescription });
    ['name="robots"', 'property="og:title"', 'property="og:description"', 'property="og:type"',
     'property="og:url"', 'property="og:image"', 'name="twitter:card"'].forEach(selector => this.meta.removeTag(selector));
    this.canonicalLink?.remove();
  }

  get language(): Language {
    const lang = this.translate.getCurrentLang();
    return isSupportedLanguage(lang) ? lang : DEFAULT_LANGUAGE;
  }

  /** The open position the candidate is applying to, if the offer is one of the listed ones */
  get selectedPosition(): OpenPosition | undefined {
    return this.positions.find(position => position.id === this.offer);
  }

  isNewPosition(position: OpenPosition): boolean {
    const ageInDays = (Date.now() - new Date(position.publishedAt).getTime()) / 86_400_000;
    return ageInDays <= 7;
  }

  /** Bring the form into view after picking a position (the ?offer= change comes from the routerLink) */
  scrollToForm() {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => this.document.getElementById('form-title')?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    }
  }

  /**
   * Page-level SEO tags. The page is deliberately kept out of search results (noindex);
   * the rest is ready in case it is indexed some day, and also feeds link previews.
   */
  private setSeoTags() {
    // Keep this page out of search results
    this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: this.canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: 'https://conectio.es/assets/favicon-512x512.png' });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary' });

    // Reuse the server-rendered link after hydration instead of adding a second one
    this.canonicalLink = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]') ?? this.document.createElement('link');
    this.canonicalLink.setAttribute('rel', 'canonical');
    this.canonicalLink.setAttribute('href', this.canonicalUrl);
    this.document.head.appendChild(this.canonicalLink);

    this.seoSubscription = this.translate.stream(['register.pageTitle', 'register.pageDescription']).subscribe(t => {
      // stream() emits the raw keys until translations are loaded
      if (t['register.pageTitle'] === 'register.pageTitle') {
        return;
      }
      this.title.setTitle(t['register.pageTitle']);
      this.meta.updateTag({ name: 'description', content: t['register.pageDescription'] });
      this.meta.updateTag({ property: 'og:title', content: t['register.pageTitle'] });
      this.meta.updateTag({ property: 'og:description', content: t['register.pageDescription'] });
    });
  }

  private markAllAsTouched() {
    Object.values(this.registerForm.controls).forEach(control => {
      control.markAsTouched();
    });
  }

  storeNameAndPhoneAndNavigate(linkedin: boolean) {
    if (this.isLoading) {
      return;
    }
    if (this.registerForm.valid) {
      this.isLoading = true;
      this.errorPhone = false;

      const name = this.registerForm.get('name')?.value;
      const phone = this.registerForm.get('phone')?.value;
      document.cookie = `name=${name}; path=/;`;
      document.cookie = `phone=${phone}; path=/;`;


      this.http.post(`${environment.url}/api/v1/user/is_registered`, { 'phone': phone, "offer_registry": this.offer }).subscribe({
        next: (response: any) => {
          console.log(response);
          if (response.is_registered) {
            this.isLoading = false;
            this.errorPhone = true;

            document.cookie = `name=${name}; path=/;`;
            document.cookie = `phone=${phone}; path=/;`;
            document.cookie = `access_token=${response.access_token}; path=/;`;
            document.cookie = `manualStep=5; path=/;`;

            // if the user is registered, we need to navigate to the login page
            this.router.navigate(['/session/candidate-profiling'], { queryParams: { is_registered: true } });



          }
          else {
            
            if (linkedin) {
              window.location.href = this.linkedinUrl;
            } else {
              this.http.post(`${environment.url}/api/preregister`, { 
                'name': this.registerForm.get('name')?.value, 
                'phone': this.registerForm.get('phone')?.value,
                'offer': this.offer
              }).subscribe({
                next: (response: any) => {
                  let manualStep = response.step_register;
                  document.cookie = `name=${name}; path=/;`;
                  document.cookie = `phone=${phone}; path=/;`;
                  document.cookie = `manualStep=${manualStep}; path=/;`;
                  document.cookie = `access_token=${response.access_token}; path=/;`;
                  // Navigate to the next step or page
                  this.router.navigate(['/session/register/manual']);

                },
                error: (error: any) => {
                    console.error('Error:', error);  
                    this.isLoading = false;
                }
              });
              
            }
          }
        },
        error: (error: any) => {
          console.error('Error:', error);
          this.isLoading = false;
          this.errorPhone = true;
        }
      });

    } else {
      this.markAllAsTouched();
      console.error('Form is invalid');
    }
  }


}